import { Component, EventEmitter, Injector, Input, OnInit, Output, InjectionToken } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
// services
import * as languageLibrary from '../../../../services/language';
import { MasterService } from '../../../../services/master.service';
import { MessageService } from '../../../../services/message.service';
// external
import { ActivatedRoute } from '@angular/router';
import moment from 'moment';
import { PAYMENT_CYCLES } from '../../../../constants/sale'
import { Lendor } from 'src/app/models/lendor.model';
import { FloorPlan } from 'src/app/models/floor-plan.model';
import { FloorPlanReduction } from 'src/app/models/floor-plan-reduction.model';
import { formatCurrency, formatPercent } from 'src/app/utils/format';

import { clean } from 'src/app/utils/numeric';
import { minValueValidator } from 'src/app/utils/min-value.validator';
@Component({
    selector: 'inventory-floor-plans',
    templateUrl: './inventory-floor-plans.component.html',
    styleUrls: ['./inventory-floor-plans.component.scss'],
    standalone: false
})
export class InventoryFloorPlansComponent implements OnInit {
    /*
      * Variables
      */
    
    // save the language
    @Input() language: string = localStorage.getItem('language') ? localStorage.getItem('language') : 'EN'
    
    // define the inventoryId
    @Input() inventoryId: number = 0
    
    @Input() purchaseCost: number = 0;
    
    @Input() dateOnLot: Date

    @Output() emitterInformation$: EventEmitter<Object> = new EventEmitter()

    // define the balance
    private balance: number | null = 0;

    private fpCostTotal: number = 0;
    
    // define the lenders
    protected lendors: Lendor[] = []
    
    //  define if some changes not saved
    private savePending: boolean = false
    
    // set all words
    protected words = languageLibrary.language
    
    protected paymentFrequency: string[] = PAYMENT_CYCLES
    
    // define expanded
    protected expanded: boolean = true
    
    // showCreateForm
    protected showCreateForm: boolean = true
    
    protected floorPlans: FloorPlan[];
    
    protected floorPlanReductionLines: FloorPlanReduction[];
    
    protected inventoryInfo: any;
    
    protected floorPlanDisplayed: FloorPlan | null = null;
    
    protected activeFloorPlanExists: any
    
    protected floorPlanTotals: any = null;
    
    private floorPlanCostTotal: number;
    
    protected selectedId: number | null = null;
    
    private deleteId: number | null = null;
    
    protected information = new FormGroup({
        id: new FormControl<number | null>(null),
        initialAmount: new FormControl<number>(0, [Validators.required, Validators.min(1)]),
        reductionAmount: new FormControl<number>(0, [Validators.min(0)]),
        initialFeeAmount: new FormControl<number>(0, [Validators.min(0)]),
        feeAmount: new FormControl<number>(0, [Validators.min(0)]),
        dateOnFloorPlan: new FormControl<string | null>(null, [Validators.required]),
        accountNumber: new FormControl<string | null>(null),
        reductionFrequency: new FormControl<string | null>(null),
        apr: new FormControl<number>(0, [Validators.required, Validators.min(0)]),
        daysAllowed: new FormControl<number>(0, [Validators.required, Validators.min(0)]),
        vendorId: new FormControl<number | null>(null, [Validators.required]),
        inventoryId: new FormControl<number | null>(null, [Validators.required]),
        contactFloorPlanId: new FormControl<number | null>(null, [Validators.required]),
        daysPerYear: new FormControl<number>(365, [Validators.required]),
    })

    protected informationReduce = new FormGroup({
        reductionAmount: new FormControl<string>('', [Validators.required, Validators.pattern('^[0-9]*(\.[0-9]+)?$'), Validators.min(0.01)]),
        datePaid: new FormControl<string>(moment().format('YYYY-MM-DD'), [Validators.required]),
        feeAmount: new FormControl<string>(formatCurrency(0)),
        financeCharges: new FormControl<string>(formatCurrency(0), [minValueValidator(0)]),
        floorPlanId: new FormControl<number | null>(null),
        id: new FormControl<number | null>(null),
        status: new FormControl<string>("Reduction"),
        prevBalance: new FormControl<string>(formatCurrency(0)),
        apr: new FormControl<string>(formatPercent(0), [Validators.required, Validators.min(0)]),
    },
        {
            validators: Validators.compose([this.dateLessThan('datePaid'), this.amountLessThan('reductionAmount', 'prevBalance', 'id')])
        }
    )

    protected floorPlanTotalSum: number = 0;
    
    private lenderAPR: number = 0;
    
    protected modify: boolean = false;
    
    private myInjector: Injector;
    
    protected activeComponent: any;

    protected loading: boolean = false
    
    get companyName() { return this.information.get('companyName') }
    get accountNumber() { return this.information.get('accountNumber') }
    get dateOnFloorPlan() { return this.information.get('dateOnFloorPlan') }
    get initialAmount() { return this.information.get('initialAmount') }
    get reductionAmount() { return this.information.get('reductionAmount') }
    get daysAllowed() { return this.information.get('daysAllowed') }
    get recurrentFeeFrecuency() { return this.information.get('recurrentFeeFrecuency') }
    get feeAmount() { return this.information.get('feeAmount') }
    get initialFeeAmount() { return this.information.get('initialFeeAmount') }
    get apr() { return this.information.get('apr') }
    /*
   * life cycles
   */

    constructor(private route: ActivatedRoute, private master: MasterService, private ms: MessageService, private injector: Injector) {

    }

    ngOnInit() {
        if (this.route.snapshot.params["id"] === 'add') {
            return
        }

        this.getFloorPlan()
    }

    ngOnChanges(): void {
        //Called before any other lifecycle hook. Use it to inject dependencies, but avoid any serious work here.
        //Add '${implements OnChanges}' to the class.
        this.inventoryInfo = { purchaseCost: this.purchaseCost, dateOnLot: this.dateOnLot }
        this.setInventoryInformation(this.inventoryInfo)
    }

    /*
   * Functions
   */

    public dateLessThan(datePaid: string) {
        return (group: FormGroup): { [key: string]: any } => {
            let paid = group.controls[datePaid];
            if (!this.floorPlanDisplayed) {
                return
            }

            if (paid.value == "") {
                this.informationReduce.controls['datePaid'].setErrors({ 'datePaid': `Error! The reduction date cannot be empty.` });
                return {
                    dates: "The reduction date cannot be empty."
                };
            }
            
            let dateOnFP = this.floorPlanDisplayed['dateOnFloorPlan']
            if (paid.value < dateOnFP) {
                this.informationReduce.controls['datePaid'].setErrors({ 'datePaid': `Error! The reduction date cannot be before the Date on Floor Plan ${moment(dateOnFP).format('MM-DD-YYYY')}.` });
                return {
                    dates: "The reduction date cannot be before the Date on Floor Plan."
                };
            }
            
            if (paid.value > moment().format()) {
                this.informationReduce.controls['datePaid'].setErrors({ 'datePaid': `Error! The reduction date cannot be in the future.` });
                return {
                    dates: "The reduction date cannot be in the future."
                };
            }
            
            return {}
        }
    }

    public amountLessThan(reductionAmount: string, prevBalance: string, id: string) {
        return (group: FormGroup): { [key: string]: any } => {
            const amt = clean(group.controls[reductionAmount].value);
            const previousBalance = clean(group.controls[prevBalance].value);
            const redId = parseInt(group.controls[id].value);
            
            if (!this.floorPlanDisplayed || !amt) {
                return
            }

            if (redId !== null) {
                if (amt > previousBalance) {
                    this.informationReduce.controls['reductionAmount'].setErrors({ 'reductionAmount': `Error! The reduction amount cannot be more than the current payoff balance.` });
                    return {
                        amt: `The reduction amount cannot be more than the current payoff balance.`
                    };
                }
                return
            }

            if (amt > this.balance) {
                this.informationReduce.controls['reductionAmount'].setErrors({ 'reductionAmount': `Error! The reduction amount cannot be more than the current payoff balance.` });
                return {
                    amt: `The reduction amount cannot be more than the current payoff balance.`
                };
            }
            return {};
        }
    }

    public getContactInfo() {
        let vendorId = this.information.get("vendorId").value
        if (isNaN(vendorId)) {
            return
        }
        this.master.get(`contacts/${vendorId}/floorplan`, res => {
            if (!res) {
                this.ms.sendMessage("alert", { type: "danger", text: this.words[this.language]['apiNoResponse'] })
                return
            }

            if (res.status !== 200) {
                this.ms.sendMessage("alert", { type: "danger", text: res.data.error });
                return
            }

            if (!res.data.contactFloorPlan) {
                this.information.patchValue({
                    reductionAmount: null,
                    initialFeeAmount: 0,
                    feeAmount: 0,
                    accountNumber: null,
                    reductionFrequency: null,
                    apr: 0,
                    daysAllowed: 0
                })
                return
            }

            this.setLenderInformation(res.data.contactFloorPlan)
        })
    }

    public getFloorPlanLendors() {
        this.master.get(`contacts?type=floorPlanVendor`, (res) => {
            if (!res || !res.data) {
                this.ms.sendMessage("alert", { type: "danger", text: this.words[this.language]['apiNoResponse'] });
                return
            }

            if (res.status !== 200) {
                this.ms.sendMessage("alert", { type: "danger", text: res.data.error });
                return
            }

            this.lendors = res.data.contacts
        })
    }

    public getFloorPlan() {
        this.loading = true
        this.master.get(`inventory/${this.inventoryId}/floorPlan`, (res) => {
            this.loading = false

            if (!res || !res.data) {
                this.ms.sendMessage("alert", { type: "danger", text: this.words[this.language]['apiNoResponse'] });
                return
            }

            if (res.status !== 200) {
                this.ms.sendMessage("alert", { type: "danger", text: res.data.error });
                return
            }

            if (!res.data.floorPlan) {
                this.updateFPCostOnGeneralTab(0)
                return
            }

            this.floorPlans = res.data.floorPlan
            this.floorPlanTotalSum = res.data.floorPlanTotalSum
            this.updateFPCostOnGeneralTab(this.floorPlanTotalSum ? this.floorPlanTotalSum : 0)
            this.showCreateForm = false
            if (!this.floorPlans) {
                this.showCreateForm = true
                return
            }

            const activeIndex = this.floorPlans.findIndex((fp) => fp['status'] === 'Active')
            if (activeIndex < 0) {
                this.showCreateForm = true
                return
            }

            this.selectFloorPlan(activeIndex)
        })
    }

    public resetFloorPlan() {
        this.floorPlanReductionLines = null
        this.floorPlanDisplayed = null
        this.floorPlanTotals = null
        this.floorPlanTotalSum = 0
        this.balance = null
        this.selectedId = null
        this.updateFPCostOnGeneralTab(0)
        this.resetInformationReduce()
        this.resetInformation(true)
    }

    public selectFloorPlan(index: number) {
        if (!this.floorPlans || this.floorPlans.length < index) {
            return
        }

        this.floorPlanDisplayed = this.floorPlans[index]
        if (!this.floorPlanDisplayed) {
            return
        }

        this.floorPlanReductionLines = []
        if (this.floorPlanDisplayed['reductionLines'] && this.floorPlanDisplayed['reductionLines']['finalReductionLines']) {
            this.floorPlanReductionLines = this.floorPlanDisplayed['reductionLines']['finalReductionLines']        
        }

        this.floorPlanTotals = null
        if (this.floorPlanDisplayed['reductionLines'] && this.floorPlanDisplayed['reductionLines']['floorPlanTotals']) {
            this.floorPlanTotals = this.floorPlanDisplayed['reductionLines']['floorPlanTotals']
        }

        this.balance = this.floorPlanDisplayed.initialAmount
        if (this.floorPlanTotals && this.floorPlanTotals['currentBalance']) {
            this.balance = clean(this.floorPlanTotals['currentBalance']);
        } 

        this.setInformationReduce(this.floorPlanDisplayed, false)
        this.setDates(this.floorPlanDisplayed['dateOnFloorPlan'], this.floorPlanDisplayed['daysAllowed'])
    }

    public setDates(dateOnFloorPlan, daysAllowed) {
        let today = moment(moment().format('YYYY-MM-DD'))
        let daysOnFloorPlan = 0
        let daysRemaining = 0
        let daysPastLimit = 0
        if (!dateOnFloorPlan) {
            return
        }
        daysOnFloorPlan = today.diff(dateOnFloorPlan, 'days') + 1
        daysRemaining = (daysAllowed - daysOnFloorPlan) + 1
        if (daysRemaining < 0) {
            daysRemaining = 0
        }
        const lastDateOnFloorPlan = new Date(dateOnFloorPlan);
        lastDateOnFloorPlan.setDate(lastDateOnFloorPlan.getDate() + (daysAllowed + 1));
        if (lastDateOnFloorPlan < new Date()) {
            daysPastLimit = (today.diff(lastDateOnFloorPlan, 'days') + 1) * -1
        }
        this.floorPlanDisplayed['daysOnFloorPlan'] = daysOnFloorPlan
        this.floorPlanDisplayed['daysRemaining'] = daysRemaining
        this.floorPlanDisplayed['daysPastLimit'] = daysPastLimit
        this.floorPlanDisplayed['lastDateOnFloorPlan'] = moment(lastDateOnFloorPlan).format('YYYY-MM-DD')
    }

    public setInformationReduce(floorPlanData: any, changeButtonLabel: boolean) {
        this.selectedId = null
        this.informationReduce.reset()
        this.informationReduce.get('reductionAmount').setValidators([Validators.required])
        this.informationReduce.get('datePaid').setValidators([Validators.required])
        this.informationReduce.get('status').setValue("Reduction")
        this.informationReduce.get('feeAmount').setValue(formatCurrency(floorPlanData['feeAmount']))
        this.informationReduce.get('financeCharges').setValue(formatCurrency(0))
        this.informationReduce.get('prevBalance').setValue(formatCurrency(this.balance))
        if (changeButtonLabel) {
            document.getElementById('btn-add-reduction').innerHTML = "Modify Reduction"
            this.selectedId = floorPlanData['id']
            this.informationReduce.get('id').setValue(floorPlanData['id'])
            this.informationReduce.get('floorPlanId').setValue(floorPlanData['floorPlanId'])
            this.informationReduce.get('datePaid').setValue(moment(floorPlanData['datePaid']).format('YYYY-MM-DD'))
            this.informationReduce.get('apr').setValue(formatPercent(floorPlanData['reductionAPR']))
            this.informationReduce.get('reductionAmount').setValue(formatCurrency(floorPlanData['reductionAmount']))
            if (floorPlanData['status'] == 'Initial') {
                this.informationReduce.get('reductionAmount').setValidators(null)
                this.informationReduce.get('datePaid').setValidators(null)
                this.informationReduce.get('status').setValue(floorPlanData['status'])
            }
        } else {
            this.informationReduce.get('floorPlanId').setValue(floorPlanData['id'])
            this.informationReduce.get('datePaid').setValue(moment().format('YYYY-MM-DD'))
            this.informationReduce.get('apr').setValue(formatPercent(floorPlanData['apr']))
            parseFloat(floorPlanData['reductionAmount']) > this.balance ? this.informationReduce.get('reductionAmount').setValue(formatCurrency(this.balance)) : this.informationReduce.get('reductionAmount').setValue(formatCurrency(floorPlanData['reductionAmount']))
        }
        this.informationReduce.get('reductionAmount').updateValueAndValidity()
        this.informationReduce.get('datePaid').updateValueAndValidity()
    }

    public modifyReduction() {
        if (this.informationReduce.invalid) {
            return
        }

        if (this.informationReduce.get("financeCharges").value === undefined) {
            this.informationReduce.get("financeCharges").setValue(null)
        }

        const payload = JSON.parse(JSON.stringify(this.informationReduce.value))
        payload.reductionAmount = clean(payload.reductionAmount)
        payload.apr = clean(payload.apr)
        payload.feeAmount = clean(payload.feeAmount)
        payload.prevBalance = clean(payload.prevBalance)
        payload.financeCharges = clean(payload.financeCharges)

        this.master.put(`inventory/${this.inventoryId}/floorPlanRed/${this.informationReduce.get("id").value}`, { information: payload }, (res) => {
            if (!res || !res.data) {
                this.ms.sendMessage("alert", { type: "danger", text: this.words[this.language]['apiNoResponse'] });
                return
            }

            if (res.status !== 200) {
                this.ms.sendMessage("alert", { type: "danger", text: res.data.error });
                return
            }

            if (res.data.floorPlan.length < 1) {
                return
            }


            this.floorPlans = res.data.floorPlan
            this.floorPlanTotalSum = res.data.floorPlanTotalSum
            this.updateFPCostOnGeneralTab(this.floorPlanTotalSum ? this.floorPlanTotalSum : 0)
            this.activeFloorPlanExists = this.floorPlans.filter((fp) => fp['status'] === 'Active')
            this.showCreateForm = this.activeFloorPlanExists.length == 0 ? true : false
            this.selectFloorPlan(0);
            (document.getElementById('btn-add-reduction').innerHTML = "Add Reduction");
        })
    }

    public deleteReduction() {
        this.master.discard(`inventory/${this.inventoryId}/floorPlanRed/${this.deleteId}`, (res) => {
            if (!res || !res.data) {
                this.ms.sendMessage("alert", { type: "danger", text: this.words[this.language]['apiNoResponse'] });
                return
            }

            if (res.status !== 200) {
                this.ms.sendMessage("alert", { type: "danger", text: res.data.error });
                return
            }

            if (res.data.floorPlan.length < 1) {
                return
            }

            (document.getElementById('close-delete-modal-reduction').click())
            this.floorPlans = res.data.floorPlan
            this.floorPlanTotalSum = res.data.floorPlanTotalSum
            this.updateFPCostOnGeneralTab(this.floorPlanTotalSum ? this.floorPlanTotalSum : 0)
            this.activeFloorPlanExists = this.floorPlans.filter((fp) => fp['status'] === 'Active')
            this.showCreateForm = false
            if (this.activeFloorPlanExists.length == 0) {
                this.showCreateForm = true
                return
            }
            this.selectFloorPlan(0);
        })
    }

    public resetInformation(showForm: boolean) {
        this.information.reset()
        if (this.modify == true) {
            this.showCreateForm = false
            this.modify = false
            return
        }
        this.showCreateForm = showForm
        this.setInventoryInformation(this.inventoryInfo)
    }

    public resetInformationReduce() {
        this.informationReduce.reset();
        this.selectedId = null;
        this.deleteId = null;
        (document.getElementById('btn-add-reduction').innerHTML = "Add Reduction")
        if (!this.floorPlanDisplayed) {
            return
        }
        this.setInformationReduce(this.floorPlanDisplayed, false)
    }

    public setDeleteId(id) {
        this.deleteId = id
    }

    public saveReduction() {
        const identity = this.informationReduce.get('id').value
        const reductionAmount = parseFloat(this.informationReduce.get('reductionAmount').value)

        const remainingAmount = this.balance - reductionAmount
        if (remainingAmount <= 0) {
            this.payOff(identity, reductionAmount)
            // return
        }

        if (!identity || isNaN(identity) || (identity <= 0)) {
            this.createReduction()
            return
        }

        this.modifyReduction()
    }

    public createReduction() {
        if (this.informationReduce.invalid) {
            return
        }

        const payload = JSON.parse(JSON.stringify(this.informationReduce.value))
        payload.reductionAmount = clean(payload.reductionAmount)
        payload.apr = clean(payload.apr)
        payload.feeAmount = clean(payload.feeAmount)
        payload.prevBalance = clean(payload.prevBalance)
        payload.financeCharges = clean(payload.financeCharges)

        this.master.post(`inventory/${this.inventoryId}/floorPlanRed`, { information: payload }, (res) => {
            if (!res || !res.data) {
                this.ms.sendMessage("alert", { type: "danger", text: this.words[this.language]['apiNoResponse'] });
                return
            }

            if (res.status !== 200) {
                this.ms.sendMessage("alert", { type: "danger", text: res.data.error });
                return
            }

            this.floorPlans = res.data.floorPlan
            this.floorPlanTotalSum = res.data.floorPlanTotalSum
            this.updateFPCostOnGeneralTab(this.floorPlanTotalSum ? this.floorPlanTotalSum : 0)
            this.activeFloorPlanExists = this.floorPlans.filter((fp) => fp['status'] === 'Active')
            this.showCreateForm = false
            if (this.activeFloorPlanExists.length == 0) {
                this.showCreateForm = true
            }
            this.selectFloorPlan(0)
        })
    }

    public setLenderInformation(lenderData) {
        this.information.patchValue({
            vendorId: lenderData.contactId ? lenderData.contactId : lenderData.vendorId,
            contactFloorPlanId: lenderData.id,
            reductionAmount: lenderData.reductionAmount,
            initialFeeAmount: lenderData.initialFeeAmount,
            feeAmount: lenderData.feeAmount,
            accountNumber: lenderData.accountNumber,
            reductionFrequency: lenderData.reductionFrequency ? lenderData.reductionFrequency.toLocaleLowerCase() : null,
            apr: lenderData.apr,
            daysAllowed: lenderData.daysAllowed,
            daysPerYear: lenderData.daysPerYear
        });
        this.lenderAPR = lenderData.apr
    }

    public setInventoryInformation(inventoryData) {
        this.information.get("inventoryId").setValue(this.inventoryId)
        this.information.get("initialAmount").setValue(inventoryData.purchaseCost)
        this.information.get("dateOnFloorPlan").setValue(inventoryData.dateOnLot)
    }

    public setInformation(floorPlanData) {
        this.information.setValue({
            id: floorPlanData.id,
            daysPerYear: floorPlanData.daysPerYear,
            inventoryId: this.inventoryId,
            initialAmount: floorPlanData.initialAmount,
            dateOnFloorPlan: floorPlanData.dateOnFloorPlan,
            vendorId: floorPlanData.vendorId,
            reductionAmount: floorPlanData.reductionAmount,
            initialFeeAmount: floorPlanData.initialFeeAmount,
            feeAmount: floorPlanData.feeAmount,
            accountNumber: floorPlanData.accountNumber,
            reductionFrequency: floorPlanData.reductionFrequency ? floorPlanData.reductionFrequency.toLocaleLowerCase() : null,
            apr: floorPlanData.apr,
            daysAllowed: floorPlanData.daysAllowed,
            contactFloorPlanId: floorPlanData.contactFloorPlanId,
        })

    }

    public updateFPCostOnGeneralTab(floorPlanCostTotal: number) {
        this.floorPlanCostTotal = floorPlanCostTotal
        this.emitterInformation$.emit({ message: 'setFPCostTotal', floorPlanCostTotal: floorPlanCostTotal })
    }

    public save() {
        const identity = this.information.get('id').value
        this.showCreateForm = false

        if (!identity || isNaN(identity) || (identity <= 0)) {
            this.createFloorPlan()
            return
        }

        this.modifyFloorPlan()
    }

    public modifyFloorPlan() {
        if (this.information.invalid || !this.floorPlanDisplayed) {
            return
        }

        const payload = JSON.parse(JSON.stringify(this.information.value))
        payload.initialAmount = clean(payload.initialAmount)
        payload.apr = clean(payload.apr)
        payload.feeAmount = clean(payload.feeAmount)
        payload.initialFeeAmount = clean(payload.initialFeeAmount)
        delete(payload.id)

        this.master.put(`inventory/${this.inventoryId}/floorPlan/${this.floorPlanDisplayed['id']}`, { information: payload }, (res) => {
            if (!res || !res.data) {
                this.ms.sendMessage("alert", { type: "danger", text: this.words[this.language]['apiNoResponse'] });
                return
            }

            if (res.status !== 200) {
                this.ms.sendMessage("alert", { type: "danger", text: res.data.error });
                return
            }

            this.floorPlans = res.data.floorPlan
            this.floorPlanTotalSum = res.data.floorPlanTotalSum
            this.updateFPCostOnGeneralTab(this.floorPlanTotalSum ? this.floorPlanTotalSum : 0)
            this.activeFloorPlanExists = this.floorPlans.filter((fp) => fp['status'] === 'Active')
            this.showCreateForm = false
            if (this.activeFloorPlanExists.length == 0) {
                this.showCreateForm = true
                return
            }
            this.selectFloorPlan(0)
            this.resetInformation(false)
        })
    }

    public createFloorPlan() {
        if (this.information.invalid) {
            return
        }

        const payload = JSON.parse(JSON.stringify(this.information.value))
        payload.initialAmount = clean(payload.initialAmount)
        payload.apr = clean(payload.apr)
        payload.feeAmount = clean(payload.feeAmount)
        payload.initialFeeAmount = clean(payload.initialFeeAmount)
        delete(payload.id)

        this.master.post(`inventory/${this.inventoryId}/floorPlan`, { information: payload }, (res) => {
            if (!res || !res.data) {
                this.ms.sendMessage("alert", { type: "danger", text: this.words[this.language]['apiNoResponse'] });
                return
            }

            if (res.status !== 200) {
                this.ms.sendMessage("alert", { type: "danger", text: res.data.error });
                return
            }

            this.floorPlans = res.data.floorPlan
            this.floorPlanTotalSum = res.data.floorPlanTotalSum
            this.updateFPCostOnGeneralTab(this.floorPlanTotalSum ? this.floorPlanTotalSum : 0)
            this.activeFloorPlanExists = this.floorPlans.filter((fp) => fp['status'] === 'Active')
            this.showCreateForm = false
            if (this.activeFloorPlanExists.length == 0) {
                this.showCreateForm = true
                return
            }

            this.selectFloorPlan(0)
            this.resetInformation(false)
        })
    }

    public payOff(identity, reductionAmount) {
        this.informationReduce.get('reductionAmount').setValue(reductionAmount ? String(reductionAmount) : String(this.balance))
        this.informationReduce.get('feeAmount').setValue("0")
        this.informationReduce.get('status').setValue("Payoff")
        this.informationReduce.get('id').setValue(identity ? identity : null)
        this.informationReduce.get('financeCharges').setValue(null)
        this.showCreateForm = true
        this.activeFloorPlanExists = false
        this.createReduction()
    }

    public deleteFloorPlan() {
        if (!this.activeFloorPlanExists || this.floorPlanReductionLines.length > 1) {
            return
        }
        this.master.discard(`inventory/${this.inventoryId}/floorPlan/${this.floorPlanDisplayed['id']}`, (res) => {
            if (!res || !res.data) {
                this.ms.sendMessage("alert", { type: "danger", text: this.words[this.language]['apiNoResponse'] });
                return
            }

            if (res.status !== 200) {
                this.ms.sendMessage("alert", { type: "danger", text: res.data.error });
                return
            }

            (document.getElementById('close-delete-modal-floor-plan').click())
            this.floorPlans = res.data.floorPlan
            if (!this.floorPlans) {
                this.showCreateForm = true
                this.resetFloorPlan()
                return
            }
            this.floorPlanTotalSum = res.data.floorPlanTotalSum
            this.updateFPCostOnGeneralTab(this.floorPlanTotalSum ? this.floorPlanTotalSum : 0)
            this.showCreateForm = false

            this.activeFloorPlanExists = this.floorPlans.filter((fp) => fp['status'] === 'Active')
            if (this.activeFloorPlanExists.length == 0) {
                this.showCreateForm = true
            }
            this.selectFloorPlan(0)
        })
    }

    public modifyDefaults() {
        if (!this.activeFloorPlanExists) {
            return
        }
        this.showCreateForm = true;
        this.modify = true;
        (document.getElementById('close-modify-modal-floor-plan').click())
        this.setInformation(this.floorPlanDisplayed);
    }

    public setContactIdEvent(e) {
        if (!e) {
          return
        }
        this.information.get("vendorId").setValue(e)
        this.getContactInfo()
    }
}
